import styled, { useTheme } from 'styled-components'
import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentUser, useWidgetSession } from '@query'
import { useAIHelpContext, useBusinessContext } from '@context'
import { ValidLocale } from '@localisation'
import { useHistory, useLocation } from 'react-router-dom'
import {
  getLucaWidgetSettingsPageParams,
  getSettingsPageUrl,
  type LucaWidgetSettingsTab
} from '@constants/urls'
import { getUserDisplayName } from '@root/utils'
import {
  buildWidgetPageContext,
  getCurrentBusinessSlug,
  mergeLucaViewContext,
  type WidgetPageContext
} from './pageContext'
import { resolveWidgetSessionScope } from './sessionScope'

const WIDGET_BASE_URL = import.meta.env.VITE_AI_WIDGET_URL ?? '/widget/'

export const INIT_SESSION_TYPE = 'INIT_SESSION'

/** Must match ai-chat-widget `AI_WIDGET_TOGGLE_EMBED_WIDTH` in shared/config/env.ts */
export const AI_WIDGET_TOGGLE_EMBED_WIDTH = 'AI_WIDGET_TOGGLE_EMBED_WIDTH'
/** Must match ai-chat-widget `AI_WIDGET_CLOSE_EMBED` in shared/config/env.ts */
export const AI_WIDGET_CLOSE_EMBED = 'AI_WIDGET_CLOSE_EMBED'
/** Must match ai-chat-widget `AI_WIDGET_OPEN_LUCA_SETTINGS` in lib/config/env.ts */
export const AI_WIDGET_OPEN_LUCA_SETTINGS = 'AI_WIDGET_OPEN_LUCA_SETTINGS'
/** Must match ai-chat-widget `AI_WIDGET_SEND_PROMPT` — parent → widget welcome-style send */
export const AI_WIDGET_SEND_PROMPT = 'AI_WIDGET_SEND_PROMPT'

export interface InitSessionPayload {
  token: string | null
  origin: string
  xOrigin: string
  theme?: 'light' | 'dark'
  fontFamily?: string
  locale?: ValidLocale
  greetingName?: string | null
  isExpanded?: boolean
  /** True only when the parent side-sheet is wired to handle width toggling. */
  supportsToggleWidth?: boolean
  /** True when no specific business is selected (e.g. /start page). */
  allBusinesses?: boolean
  pageContext?: WidgetPageContext | null
  conversationId?: string
  lucaSkillsAndYolo?: boolean
}

interface Props {
  onToggleEmbedWidth?: () => void
  onClose?: () => void
  isExpanded?: boolean
  supportsToggleWidth?: boolean
  removeDividerSideRadius?: boolean
}

function parseLucaWidgetSettingsTab(tab: unknown): LucaWidgetSettingsTab | null {
  switch (tab) {
    case 'business':
    case 'connectors':
    case 'usage':
    case 'plan':
    case 'bundles':
    case 'payg':
      return tab
    default:
      return null
  }
}

function getWidgetOrigin(): string {
  if (typeof window === 'undefined') return ''
  if (WIDGET_BASE_URL.startsWith('http')) {
    try {
      return new URL(WIDGET_BASE_URL).origin
    } catch {
      return window.location.origin
    }
  }
  return window.location.origin
}

export const AIWidgetEmbed: React.FC<Props> = ({
  onToggleEmbedWidth,
  onClose,
  isExpanded,
  supportsToggleWidth,
  removeDividerSideRadius = false
}) => {
  const theme = useTheme()
  const { i18n } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const onToggleRef = useRef(onToggleEmbedWidth)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const { slug: businessSlugFromContext } = useBusinessContext()
  const routeBusinessSlug = getCurrentBusinessSlug(location.pathname)
  const businessSlug = businessSlugFromContext.trim() || routeBusinessSlug || undefined
  const {
    allBusinesses,
    pendingConversationId,
    setPendingConversationId,
    pendingLucaPrompt,
    setPendingLucaPrompt,
    viewContext,
    widgetBusinessSlugOverride
  } = useAIHelpContext()
  const widgetSessionScope = resolveWidgetSessionScope({
    allBusinesses,
    currentBusinessSlug: businessSlug,
    overrideBusinessSlug: widgetBusinessSlugOverride
  })
  const effectiveAllBusinesses = widgetSessionScope.mode === 'all-businesses'
  const effectiveBusinessSlug = widgetSessionScope.businessSlug
  const settingsSlugFromRouteOrSession = businessSlug || effectiveBusinessSlug
  const settingsNavigationSlug =
    widgetSessionScope.mode === 'notification-business'
      ? widgetSessionScope.businessSlug
      : settingsSlugFromRouteOrSession
  const sessionBusinessSlug = effectiveAllBusinesses ? undefined : effectiveBusinessSlug
  const { data: currentUser } = useCurrentUser()
  const { data: session } = useWidgetSession(Boolean(currentUser), sessionBusinessSlug)
  const greetingName = getUserDisplayName(currentUser, { fallbackToEmail: false })
  const pageContext = useMemo(
    () =>
      mergeLucaViewContext(
        buildWidgetPageContext({
          pathname: location.pathname,
          search: location.search,
          allBusinesses: effectiveAllBusinesses,
          businessSlug: effectiveBusinessSlug
        }),
        viewContext
      ),
    [location.pathname, location.search, effectiveAllBusinesses, effectiveBusinessSlug, viewContext]
  )

  const widgetLocale = Object.values(ValidLocale).includes(i18n.language as ValidLocale)
    ? (i18n.language as ValidLocale)
    : undefined

  useEffect(() => {
    onToggleRef.current = onToggleEmbedWidth
  }, [onToggleEmbedWidth])

  useEffect(() => {
    if (!onToggleEmbedWidth) return

    const handler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.origin !== getWidgetOrigin()) return
      if (event.data?.type !== AI_WIDGET_TOGGLE_EMBED_WIDTH) return
      onToggleRef.current?.()
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [onToggleEmbedWidth])

  useEffect(() => {
    if (!onClose) return

    const handler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.origin !== getWidgetOrigin()) return
      if (event.data?.type !== AI_WIDGET_CLOSE_EMBED) return
      onClose()
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [onClose])

  // Widget can open Luca settings; sheet stays open so the user can return to chat.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (event.origin !== getWidgetOrigin()) return
      if (event.data?.type !== AI_WIDGET_OPEN_LUCA_SETTINGS) return

      const tab = parseLucaWidgetSettingsTab(event.data?.tab)
      if (!tab) return
      if (!settingsNavigationSlug) return

      const next = new URL(
        getSettingsPageUrl(settingsNavigationSlug, getLucaWidgetSettingsPageParams(tab)),
        window.location.origin
      )
      if (history.location.pathname === next.pathname && history.location.search === next.search)
        return
      history.push({ pathname: next.pathname, search: next.search })
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [history, settingsNavigationSlug])

  const sendInitSession = useCallback(() => {
    const win = iframeRef.current?.contentWindow
    if (!win || typeof window === 'undefined') return

    const payload: InitSessionPayload = {
      token: session?.token ?? null,
      origin: window.location.origin,
      xOrigin: session?.country ?? 'fi',
      theme: (session?.theme as 'light' | 'dark' | undefined) ?? 'light',
      fontFamily: theme?.typography?.fontFamily?.montserrat ?? 'Montserrat, sans-serif',
      ...(widgetLocale && { locale: widgetLocale }),
      ...(greetingName && { greetingName }),
      isExpanded,
      supportsToggleWidth,
      allBusinesses: effectiveAllBusinesses,
      pageContext,
      conversationId: pendingConversationId ?? undefined,
      lucaSkillsAndYolo: session?.luca_skills_and_yolo === true
    }

    win.postMessage({ type: INIT_SESSION_TYPE, payload }, getWidgetOrigin())
    if (pendingConversationId) {
      setPendingConversationId(null)
    }
  }, [
    session?.token,
    session?.country,
    session?.theme,
    session?.luca_skills_and_yolo,
    theme?.typography?.fontFamily?.montserrat,
    widgetLocale,
    greetingName,
    isExpanded,
    supportsToggleWidth,
    effectiveAllBusinesses,
    pageContext,
    pendingConversationId,
    setPendingConversationId
  ])

  useEffect(() => {
    if (iframeLoaded && session !== undefined) {
      sendInitSession()
    }
  }, [iframeLoaded, session, sendInitSession, i18n.language])

  // Deliver a queued prompt after the iframe is ready (mirrors pendingConversationId timing).
  useEffect(() => {
    if (!iframeLoaded || session === undefined || !pendingLucaPrompt) return
    const win = iframeRef.current?.contentWindow
    if (!win) return

    win.postMessage({ type: AI_WIDGET_SEND_PROMPT, message: pendingLucaPrompt }, getWidgetOrigin())
    setPendingLucaPrompt(null)
  }, [iframeLoaded, session, pendingLucaPrompt, setPendingLucaPrompt])

  const widgetUrl = WIDGET_BASE_URL.startsWith('http')
    ? WIDGET_BASE_URL
    : WIDGET_BASE_URL.startsWith('/')
    ? WIDGET_BASE_URL
    : `/${WIDGET_BASE_URL}`

  return (
    <StyledWrapper $removeDividerSideRadius={removeDividerSideRadius}>
      <StyledIframe
        ref={iframeRef}
        src={widgetUrl}
        title="Ask Luca"
        allow="microphone"
        onLoad={() => setIframeLoaded(true)}
      />
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div<{ $removeDividerSideRadius: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: ${({ theme, $removeDividerSideRadius }) =>
    $removeDividerSideRadius ? 0 : `${theme.borderRadius.md}rem`};
  border-bottom-left-radius: ${({ theme, $removeDividerSideRadius }) =>
    $removeDividerSideRadius ? 0 : `${theme.borderRadius.md}rem`};
  overflow: hidden;
`

const StyledIframe = styled.iframe`
  outline: none;
  flex: 1;
  min-height: 0;
  border: none;
`
